import React, { useState } from "react";
import { Link } from "react-router-dom";
import SEOHead from "../../components/SEO/SEOHead";
import {
  generateBreadcrumbStructuredData,
  generateWebPageStructuredData,
} from "../../components/SEO/StructuredData";
import LegalPageLayout from "../../components/Legal/LegalPageLayout";
import { SUPPORT_EMAIL } from "../../components/SEO/siteOrigin";

const LAST_UPDATED = "October 31, 2026";
const DESCRIPTION =
  "Terms of service for Bowling Tournament App: user conduct, tournament terms, organization plans, payments, liability, and privacy.";

const TermsOfService = () => {
  const [activeSection, setActiveSection] = useState("overview");

  const structuredData = generateWebPageStructuredData({
    name: "Terms of Service",
    description: DESCRIPTION,
    url: "/terms-of-service",
    dateModified: "2026-10-31",
  });
  const breadcrumbData = generateBreadcrumbStructuredData([
    { label: "Home", path: "/" },
    { label: "Terms of Service", path: "/terms-of-service" },
  ]);

  const sections = [
    { id: "overview", title: "Overview" },
    { id: "security", title: "Security & CAPTCHA" },
    { id: "user-conduct", title: "User Conduct" },
    { id: "tournament", title: "Tournament Terms" },
    { id: "card-payments", title: "Card payments" },
    { id: "organization-plans", title: "Organization plans" },
    { id: "liability", title: "Liability & Disclaimers" },
    { id: "privacy", title: "Privacy & Data" },
    { id: "dispute-resolution", title: "Dispute Resolution & Arbitration" },
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <SEOHead
        title="Terms of Service - Bowling Tournament App"
        description={DESCRIPTION}
        keywords={[
          "terms of service",
          "user agreement",
          "tournament terms",
          "user conduct",
          "liability",
          "privacy policy",
          "data handling",
          "bowling tournament app terms",
        ]}
        canonicalUrl="/terms-of-service"
        additionalMeta={[{ name: "last-modified", content: LAST_UPDATED }]}
        structuredData={structuredData}
        additionalStructuredData={[breadcrumbData]}
      />
      <LegalPageLayout
        title="Terms of Service"
        lastUpdated={LAST_UPDATED}
        sections={sections}
        activeSection={activeSection}
        onSectionClick={scrollToSection}
      >
        <div className="space-y-12">
                {/* Overview Section */}
                <section id="overview">
                  <h2 className="heading-display text-xl mb-4">
                    Overview & Acceptance
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p>
                      Welcome to the Bowling Tournament App. These Terms of
                      Service ("Terms") govern your use of our platform,
                      including all features, services, and content available
                      through our application.
                    </p>
                    <p>
                      <strong>
                        By accessing or using our services, you agree to be
                        bound by these Terms.
                      </strong>{" "}
                      If you do not agree to these terms, please do not use our
                      application. These terms apply to all users, including
                      tournament organizers, participants, and visitors.
                    </p>
                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <p className="text-brand-charcoal">
                        <strong>Important:</strong> These terms are legally
                        binding. How we update them, and what continued use
                        means after an update, is described under Updates to
                        Terms below.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Security & CAPTCHA Section */}
                <section id="security">
                  <h2 className="heading-display text-xl mb-4">
                    Security & CAPTCHA Protection
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      CAPTCHA Implementation
                    </h3>
                    <p>
                      Our platform uses Cloudflare Turnstile CAPTCHA technology
                      to protect against automated abuse and ensure human
                      interaction. By using our services, you acknowledge and
                      agree to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-4">
                      <li>
                        Complete CAPTCHA verification when prompted during
                        registration, sign-in, and password reset processes
                      </li>
                      <li>
                        Allow CAPTCHA scripts to run in your browser for
                        security verification
                      </li>
                      <li>
                        Understand that CAPTCHA data may be processed by
                        Cloudflare in accordance with their privacy policy
                      </li>
                      <li>
                        Accept that failure to complete CAPTCHA verification may
                        prevent access to certain features
                      </li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Account Security
                    </h3>
                    <p>
                      You are responsible for maintaining the security of your
                      account credentials and for all activities that occur
                      under your account. This includes:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-4">
                      <li>Using strong, unique passwords for your account</li>
                      <li>Not sharing your login credentials with others</li>
                      <li>
                        Immediately notifying us of any unauthorized access or
                        suspicious activity
                      </li>
                      <li>
                        Logging out of your account when using shared devices
                      </li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Security Measures
                    </h3>
                    <p>
                      We implement various security measures to protect your
                      data, including but not limited to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-4">
                      <li>
                        Encryption of sensitive data in transit and at rest
                      </li>
                      <li>
                        Regular security audits and vulnerability assessments
                      </li>
                      <li>
                        Rate limiting to prevent abuse and automated attacks
                      </li>
                      <li>
                        Monitoring for suspicious activities and potential
                        security threats
                      </li>
                    </ul>
                  </div>
                </section>

                {/* User Conduct Section */}
                <section id="user-conduct">
                  <h2 className="heading-display text-xl mb-4">
                    User Conduct & Responsibilities
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Acceptable Use
                    </h3>
                    <p>
                      You agree to use our platform only for lawful purposes and
                      in accordance with these Terms. You must not:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-4">
                      <li>
                        Use the platform for any illegal or unauthorized purpose
                      </li>
                      <li>
                        Attempt to gain unauthorized access to any part of the
                        platform or other users' accounts
                      </li>
                      <li>
                        Interfere with or disrupt the platform's functionality
                        or security measures
                      </li>
                      <li>
                        Submit false, misleading, or fraudulent information
                      </li>
                      <li>
                        Use automated tools, bots, or scripts to access or
                        interact with the platform
                      </li>
                      <li>Harass, abuse, or harm other users</li>
                      <li>Violate any applicable laws or regulations</li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Information Accuracy
                    </h3>
                    <p>
                      You are responsible for providing accurate, complete, and
                      up-to-date information when:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-4">
                      <li>Creating and maintaining your account profile</li>
                      <li>Registering for tournaments or events</li>
                      <li>Submitting scores or tournament results</li>
                      <li>
                        Communicating with other users or tournament organizers
                      </li>
                    </ul>

                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <p className="text-brand-charcoal">
                        <strong>Violation Consequences:</strong> Violation of
                        these conduct rules may result in account suspension,
                        termination, or legal action as appropriate.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Tournament Terms Section */}
                <section id="tournament">
                  <h2 className="heading-display text-xl mb-4">
                    Tournament-Specific Terms
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Tournament Participation
                    </h3>
                    <p>
                      Our platform facilitates bowling tournament management and
                      participation. By participating in tournaments through our
                      platform, you agree to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-4">
                      <li>
                        Comply with all tournament rules and regulations
                        established by tournament organizers
                      </li>
                      <li>
                        Provide accurate bowling statistics, averages, and
                        personal information
                      </li>
                      <li>
                        Accept that tournament organizers have final authority
                        over tournament decisions
                      </li>
                      <li>
                        Understand that we are not responsible for tournament
                        outcomes or disputes
                      </li>
                    </ul>
                    <p className="mt-4">
                      Bowling Tournament App is not affiliated with, endorsed
                      by, or an official product of the United States Bowling
                      Congress (USBC). This app does not sanction tournaments.
                      Tournament organizers remain solely responsible for
                      compliance with USBC rules and sanctioning requirements
                      when those requirements apply.
                    </p>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Score Verification
                    </h3>
                    <p>
                      Our platform includes score verification features to
                      ensure fair play. You acknowledge that:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-4">
                      <li>
                        All submitted scores are subject to verification and
                        review
                      </li>
                      <li>
                        Discrepancies in scores may result in disqualification
                        or other penalties
                      </li>
                      <li>
                        Tournament organizers may request additional
                        verification of scores or statistics
                      </li>
                      <li>
                        We reserve the right to investigate and address
                        potential score manipulation
                      </li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Tournament Organizer Responsibilities
                    </h3>
                    <p>
                      If you are a tournament organizer using our platform, you
                      are responsible for:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-4">
                      <li>
                        Ensuring compliance with all applicable laws and
                        regulations
                      </li>
                      <li>
                        Maintaining accurate tournament information and rules
                      </li>
                      <li>
                        Handling participant disputes and concerns appropriately
                      </li>
                      <li>
                        Managing tournament fees and refunds, except Pay now
                        Cancel refunds in this app’s{" "}
                        <Link
                          to="/refund-policy"
                          className="text-brand-gold-ink hover:text-brand-charcoal underline"
                        >
                          Refund policy
                        </Link>
                      </li>
                      <li>
                        If you turn on Card payments, completing Stripe
                        onboarding. Your organization is the merchant of
                        record. Clicking Set up card payments means you
                        agree to Stripe’s Connected Account Agreement.
                        Bowling Tournament App is the software platform and
                        is not the merchant.
                      </li>
                      <li>
                        Protecting participant privacy and data in accordance
                        with our{" "}
                        <Link
                          to="/privacy-policy"
                          className="text-brand-gold-ink hover:text-brand-charcoal underline"
                        >
                          Privacy Policy
                        </Link>{" "}
                        and{" "}
                        <Link
                          to="/data-processing-agreement"
                          className="text-brand-gold-ink hover:text-brand-charcoal underline"
                        >
                          Data Processing Agreement
                        </Link>
                      </li>
                    </ul>
                  </div>
                </section>

                <section id="card-payments">
                  <h2 className="heading-display text-xl mb-4">
                    Card payments
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p>
                      Card payments is optional. Details of who is refunded
                      what, and when, are in the{" "}
                      <Link
                        to="/refund-policy"
                        className="text-brand-gold-ink hover:text-brand-charcoal underline"
                      >
                        Refund policy
                      </Link>
                      .
                    </p>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mt-8 mb-3">
                      If you run an organization
                    </h3>
                    <p>
                      If you use Set up card payments, you agree to Stripe’s
                      Connected Account Agreement. Your organization is the
                      merchant of record. Bowling Tournament App is the
                      software platform and does not hold entry money.
                    </p>
                    <p className="mt-4">
                      Turning on Card payments means bowlers who pay with Pay
                      now can Cancel until 24 hours before the squad. This app
                      refunds the advertised entry and the 2% platform fee.
                      Card processing is not refunded. You cannot turn that
                      window off. Stripe does not set the 24-hour window. After
                      that cutoff, you refund from Signup Review. Your
                      tournament rules can guide those later refunds.
                    </p>
                    <p className="mt-4">
                      Pay now estimates card processing at Stripe’s typical US
                      rate of 2.9% plus 30 cents so you net the advertised
                      entry. If Stripe takes more, you receive less than
                      advertised. If Stripe takes less, you receive more. A Pay
                      now refund still returns the advertised entry and the 2%
                      platform fee. It does not use Stripe’s actual processing.
                    </p>
                    <p className="mt-4">
                      Add to cart holds a squad spot for 24 hours. Pay now or
                      Pay at the desk closes Cart and puts them on the squad.
                      If they do neither, the hold ends and a waitlist signup
                      can take the spot. We email once when 12 hours remain.
                      Payment required to hold turns Pay at the desk off; they
                      must Pay now.
                    </p>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mt-8 mb-3">
                      If you pay with Pay now
                    </h3>
                    <p>
                      Stripe charges your card for the organization that runs
                      the tournament. Pay now includes a 2% platform fee plus
                      card processing. A Pay now refund returns the advertised
                      entry and the 2% platform fee. Card processing is not
                      refunded. Clicking Pay now means you agree to these Terms
                      and the{" "}
                      <Link
                        to="/refund-policy"
                        className="text-brand-gold-ink hover:text-brand-charcoal underline"
                      >
                        Refund policy
                      </Link>
                      .
                    </p>
                  </div>
                </section>

                <section id="organization-plans">
                  <h2 className="heading-display text-xl mb-4">
                    Organization plans
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p>
                      Organization plans are optional. Free organizations can
                      create up to 5 tournaments per year. AI Assist and
                      Priority are paid plans. Prices, yearly caps, renewal,
                      cancel, switching plans, failed payments, beta grants,
                      and refunds are in the{" "}
                      <Link
                        to="/refund-policy#organization-plans"
                        className="text-brand-gold-ink hover:text-brand-charcoal underline"
                      >
                        Refund policy
                      </Link>
                      . Subscribing on Plan agrees to these Terms and that
                      section.
                    </p>
                  </div>
                </section>

                {/* Liability Section */}
                <section id="liability">
                  <h2 className="heading-display text-xl mb-4">
                    Liability & Disclaimers
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Service Availability
                    </h3>
                    <p>
                      The service is provided on an “as available” basis. We use
                      commercially reasonable efforts to keep the platform
                      running, but we do not guarantee uninterrupted access,
                      uptime, or error-free operation. The platform may be
                      unavailable due to maintenance, updates, outages, or
                      other technical issues. There is no service-level
                      agreement unless we agree to one in a separate writing.
                    </p>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Limitation of Liability
                    </h3>
                    <p>
                      To the maximum extent permitted by law, we shall not be
                      liable for any indirect, incidental, special,
                      consequential, or punitive damages arising from your use
                      of our platform, including but not limited to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-4">
                      <li>Loss of data or information</li>
                      <li>Interruption of service</li>
                      <li>Tournament outcomes or disputes</li>
                      <li>
                        Financial losses related to tournament participation
                      </li>
                      <li>Third-party actions or content</li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Indemnification
                    </h3>
                    <p>
                      You agree to indemnify and hold harmless our platform, its
                      officers, directors, employees, and agents from any
                      claims, damages, or expenses arising from your use of the
                      platform or violation of these Terms.
                    </p>

                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <p className="text-brand-charcoal">
                        <strong>Important:</strong> These limitations do not
                        apply to damages caused by our gross negligence or
                        intentional misconduct, or where prohibited by
                        applicable law.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Privacy & Data Section */}
                <section id="privacy">
                  <h2 className="heading-display text-xl mb-4">
                    Privacy & Data Protection
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Data Collection & Use
                    </h3>
                    <p>
                      Your privacy is important to us. Our collection, use, and
                      protection of your personal data is governed by our{" "}
                      <Link
                        to="/privacy-policy"
                        className="text-brand-gold-ink hover:text-brand-charcoal underline"
                      >
                        Privacy Policy
                      </Link>
                      , which is incorporated into these Terms by reference. By
                      using our services, you agree to the terms of our Privacy
                      Policy.
                    </p>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Data Retention
                    </h3>
                    <p>
                      Our data retention practices are detailed in our{" "}
                      <Link
                        to="/data-retention-policy"
                        className="text-brand-gold-ink hover:text-brand-charcoal underline"
                      >
                        Data Retention Policy
                      </Link>
                      . We retain your data for as long as necessary to provide
                      our services and comply with legal obligations. Tournament
                      data may be retained for historical and statistical
                      purposes, subject to your rights under applicable privacy
                      laws.
                    </p>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Tournament Organizer Responsibilities
                    </h3>
                    <p>
                      If you are a tournament organizer, you must accept our{" "}
                      <Link
                        to="/data-processing-agreement"
                        className="text-brand-gold-ink hover:text-brand-charcoal underline"
                      >
                        Data Processing Agreement (DPA)
                      </Link>{" "}
                      before processing bowler data. The DPA outlines your
                      responsibilities as a Data Controller, including data
                      security, permitted uses, and breach notification
                      requirements. By accepting the DPA, you agree to its terms,
                      which are incorporated into these Terms by reference.
                    </p>
                    <p className="mt-4">
                      Card payments is optional. If you use Set up card
                      payments, you agree to Stripe’s Connected Account
                      Agreement. Your organization is the merchant of record.
                      Bowling Tournament App does not hold entry money.
                    </p>

                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <p className="text-brand-charcoal">
                        <strong>Important:</strong> Tournament organizers act as
                        Data Controllers for bowler data they collect and are
                        responsible for ensuring they have proper authorization
                        to enter and process personal information. The platform
                        acts only as a Data Processor.
                      </p>
                    </div>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Third-Party Services
                    </h3>
                    <p>
                      Our platform may integrate with third-party services (such
                      as Cloudflare for CAPTCHA). These services have their own
                      privacy policies and terms of service that govern their
                      collection and use of your data.
                    </p>
                  </div>
                </section>

                {/* Dispute Resolution & Arbitration Section */}
                <section id="dispute-resolution">
                  <h2 className="heading-display text-xl mb-4">
                    Dispute Resolution & Arbitration
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Agreement to Arbitrate
                    </h3>
                    <p className="mb-4">
                      You and Bowling Tournament App agree that any dispute,
                      claim, or controversy arising out of or relating to these
                      Terms, the platform, or your use of our services
                      (collectively, "Disputes") will be resolved through
                      binding arbitration, rather than in court, except that you
                      may assert claims in small claims court if your claims
                      qualify.
                    </p>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Arbitration Rules and Procedures
                    </h3>
                    <p className="mb-4">
                      The arbitration will be conducted by the American
                      Arbitration Association ("AAA") under its Consumer
                      Arbitration Rules, or as otherwise mutually agreed upon.
                      The AAA's rules are available at{" "}
                      <a
                        href="https://www.adr.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-gold-ink hover:text-brand-charcoal underline"
                      >
                        www.adr.org
                      </a>
                      . The arbitrator's decision will be final and binding.
                    </p>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Class Action Waiver
                    </h3>
                    <p className="mb-4">
                      You and Bowling Tournament App agree that any arbitration
                      will be conducted on an individual basis and not in a
                      class, collective, consolidated, or representative
                      proceeding. You agree that you may bring claims against us
                      only in your individual capacity and not as a plaintiff or
                      class member in any purported class or representative
                      proceeding.
                    </p>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Location and Governing Law
                    </h3>
                    <p className="mb-4">
                      The arbitration will be conducted in the State of
                      California, United States, unless you and we agree
                      otherwise. This Agreement and any Disputes will be
                      governed by the laws of the State of California, without
                      regard to its conflict of law provisions.
                    </p>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Severability
                    </h3>
                    <p>
                      If any part of this arbitration agreement is found to be
                      unenforceable, the remainder will remain in full force
                      and effect. If the class action waiver is found to be
                      unenforceable, then the entire arbitration agreement will
                      be unenforceable, and disputes will be resolved in court.
                    </p>
                  </div>
                </section>

                {/* Contact & Updates Section */}
                <section className="border-t border-brand-charcoal/10 pt-8">
                  <h2 className="heading-display text-xl mb-4">
                    Contact & Updates
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Questions or Concerns?
                    </h3>
                    <p>
                      If you have any questions about these Terms of Service,
                      please contact us:
                    </p>
                    <p className="font-medium">
                      Email: {SUPPORT_EMAIL}
                    </p>
                    <p className="text-sm text-brand-muted">
                      We aim to respond as soon as reasonably practicable.
                    </p>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Updates to Terms
                    </h3>
                    <p>
                      We may update these Terms from time to time to reflect
                      changes in our services or legal requirements. Material
                      changes are posted on this page with an updated “Last
                      Updated” date and an effective date when stated. We may
                      also email registered users when that is practical; posting
                      on this page is the notice that controls. Continued use of
                      the service after the effective date means you accept the
                      updated Terms. If you disagree, stop using the service and
                      cancel any organization plan under the{" "}
                      <Link
                        to="/refund-policy#organization-plans"
                        className="text-brand-gold-ink hover:text-brand-charcoal underline"
                      >
                        Refund policy
                      </Link>
                      .
                    </p>
                    <p className="font-medium mt-4">
                      Last Updated: {LAST_UPDATED}
                    </p>
                  </div>
                </section>
        </div>
      </LegalPageLayout>
    </>
  );
};

export default TermsOfService;
